import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FormBrandOverride,
  FormConsentMode,
  FormConsentSnapshot,
  ResolvedFormBrand,
  SurveyAudienceMode,
  SurveyDedupeKey,
  SurveyFlowValidationResult,
  SurveyPageDefinition,
  SurveyResponseLimit,
  SurveyStatus,
} from '@engage/types';
import { apiClient } from './client';

// ─── REST types (mirror api/src/modules/surveys) ──────────────────────────────

export interface Survey {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  slug: string;
  status: SurveyStatus;
  audience_mode: SurveyAudienceMode;
  response_limit: SurveyResponseLimit;
  dedupe_key: SurveyDedupeKey;
  brand_override: FormBrandOverride | null;
  consent_mode: FormConsentMode | null;
  consent_text: string | null;
  reminder_offsets: number[];
  max_reminders: number;
  current_draft_version_id: string | null;
  current_published_version_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Completed responses — present on the list endpoint only. */
  response_count?: number;
}

export interface SurveyVersion {
  id: string;
  survey_id: string;
  version_number: number;
  pages: SurveyPageDefinition[];
  brand_snapshot: ResolvedFormBrand | Record<string, never>;
  consent_snapshot: FormConsentSnapshot | null;
  published_at: string | null;
  created_at: string;
}

export interface SurveyDetail {
  survey: Survey;
  current_version: SurveyVersion | null;
  published_version: SurveyVersion | null;
}

export interface NewSurveyInput {
  name: string;
  description?: string;
  audience_mode?: SurveyAudienceMode;
  slug?: string;
}

export interface SurveyPatchInput {
  name?: string;
  description?: string | null;
  slug?: string;
  audience_mode?: SurveyAudienceMode;
  response_limit?: SurveyResponseLimit;
  dedupe_key?: SurveyDedupeKey;
  brand_override?: FormBrandOverride | null;
  consent_mode?: FormConsentMode | null;
  consent_text?: string | null;
  reminder_offsets?: number[];
  max_reminders?: number;
}

const keys = {
  list: (filters?: { status?: SurveyStatus; search?: string }) =>
    ['surveys', filters ?? {}] as const,
  detail: (id: string) => ['surveys', id] as const,
  validate: (id: string) => ['surveys', id, 'validate'] as const,
};

// ─── Hooks ─────────────────────────────────────────────────────────────────────

export function useSurveys(filters?: { status?: SurveyStatus; search?: string }) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: async () => {
      const r = await apiClient.get<{ data: Survey[] }>('/surveys', { params: filters });
      return r.data.data;
    },
  });
}

export function useSurvey(id: string | undefined) {
  return useQuery({
    queryKey: id ? keys.detail(id) : ['surveys', 'none'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: SurveyDetail }>(`/surveys/${id}`);
      return r.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewSurveyInput) => {
      const r = await apiClient.post<{ data: Survey }>('/surveys', input);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export function useUpdateSurvey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SurveyPatchInput) => {
      const r = await apiClient.patch<{ data: Survey }>(`/surveys/${id}`, patch);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSaveSurveyDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { pages: SurveyPageDefinition[] }) => {
      const r = await apiClient.put<{ data: SurveyVersion }>(`/surveys/${id}/draft`, body);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.detail(id) }),
  });
}

/**
 * Full flow-validation result for the draft. The builder's publish panel reads
 * this so it shows the same verdict the publish gate enforces. Disabled until
 * explicitly enabled (the modal opens it on demand after a draft flush).
 */
export function useValidateSurvey(id: string | undefined, enabled = false) {
  return useQuery({
    queryKey: id ? keys.validate(id) : ['surveys', 'none', 'validate'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: SurveyFlowValidationResult }>(`/surveys/${id}/validate`);
      return r.data.data;
    },
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

export function usePublishSurvey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post<{ data: SurveyVersion }>(`/surveys/${id}/publish`);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useArchiveSurvey(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post(`/surveys/${id}/archive`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

// ─── Results (Task 5) ──────────────────────────────────────────────────────────

export type SurveyCell =
  | { state: 'value'; value: string }
  | { state: 'skipped_optional' }
  | { state: 'skipped_not_shown' };

export interface SurveyResponsesResult {
  survey: { id: string; name: string };
  columns: { key: string; label: string }[];
  rows: { id: string; completed_at: string | null; source: string; cells: Record<string, SurveyCell> }[];
  total: number;
  capped: boolean;
}

export type SurveyQuestionAggregate =
  | { key: string; label: string; type: 'NUMERIC_SCALE' | 'LIKERT'; answered: number; mean: number | null; histogram: Record<string, number> }
  | { key: string; label: string; type: 'NPS'; answered: number; promoters: number; passives: number; detractors: number; score: number | null; histogram: Record<string, number> }
  | { key: string; label: string; type: 'SINGLE_SELECT' | 'MULTI_SELECT'; answered: number; options: { value: string; count: number; pct: number }[] }
  | { key: string; label: string; type: 'TEXT'; answered: number };

export interface SurveyAggregateResult {
  total_responses: number;
  questions: SurveyQuestionAggregate[];
}

export interface SurveyFunnelRow {
  page_id: string;
  views: number;
  completed: number;
  completion_rate: number;
}

export function useSurveyResponses(id: string | undefined, limit = 100) {
  return useQuery({
    queryKey: ['surveys', id, 'responses', limit],
    queryFn: async () => {
      const r = await apiClient.get<{ data: SurveyResponsesResult }>(`/surveys/${id}/responses`, { params: { limit } });
      return r.data.data;
    },
    enabled: !!id,
  });
}

export function useSurveyAggregate(id: string | undefined) {
  return useQuery({
    queryKey: ['surveys', id, 'aggregate'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: SurveyAggregateResult }>(`/surveys/${id}/aggregate`);
      return r.data.data;
    },
    enabled: !!id,
  });
}

export function useSurveyFunnel(id: string | undefined) {
  return useQuery({
    queryKey: ['surveys', id, 'funnel'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { pages: SurveyFunnelRow[] } }>(`/surveys/${id}/funnel`);
      return r.data.data.pages;
    },
    enabled: !!id,
  });
}

/**
 * Authenticated file download for CSV/PDF export. The export routes sit behind
 * the API's auth middleware, so a plain <a href> wouldn't carry the bearer
 * token — fetch as a blob via the shared client, then trigger a browser save.
 */
export async function downloadSurveyExport(id: string, format: 'csv' | 'pdf'): Promise<void> {
  const res = await apiClient.get(`/surveys/${id}/export.${format}`, { responseType: 'blob' });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `survey-${id}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
